import { useVideoPlayerGroupContext } from "components/scrapEyeDataDashboard/VideoPlayer/contexts/VideoPlayerGroupContext";
import { useHotkeyContext } from "components/scrapEyeDataDashboard/VideoPlayer/contexts/HotkeyContext";
import { useVideoControlState } from "components/scrapEyeDataDashboard/VideoPlayer/contexts/VideoControlContext";
import { useLabellingKeyboard } from "components/scrapEyeLabelling/hooks/useLabellingKeyboard";
import { PendingAreaSelection } from "components/scrapEyeLabelling/hooks/usePendingAreaSelection";
import { AreaMaterialSnapshot } from "components/scrapEyeLabelling/types";
import { useMaterials } from "hooks/useMaterials";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useHotkeys, useHotkeysContext } from "react-hotkeys-hook";
import {
  MaterialRead,
  RailcarSlotMaterialSpan,
} from "src/store/api/generatedApi";
import { useManualReviewAreaHotkeyBehaviour } from "src/hooks/settings";
import {
  useAreaMaterialColors,
  useAreaMaterialLabels,
  useReportedRailcarMaterials,
} from "components/scrapEyeLabelling/hooks/useReportedRailcarMaterials";
import { useManualReviewEvents } from "./contexts/ManualReviewEventContext";
import {
  EVENT_LABELLING_DELETE_SCOPE,
  MANUAL_REVIEW_SCOPE,
} from "./hotkeyScopes";

type ManualReviewHotkeysProps = Readonly<{
  onVerifyAndNext: () => Promise<void>;
  location: string;
  railcarSlotMaterials?: RailcarSlotMaterialSpan[];
}>;

type ManualReviewHotkeysResult = Readonly<{
  pendingAreaSelection: PendingAreaSelection | null;
  clearPendingAreaSelection: () => void;
  handleAreaSelect: (area: string) => void;

  isAreaModeActive: boolean;
  isShiftHeld: boolean;
  binEntryActive: boolean;
  binBuffer: string;
  binEntryAvailable: boolean;

  filteredMaterials: readonly MaterialRead[];

  isAreaMaterialMappingOpen: boolean;
  openAreaMaterialMappingModal: () => void;
  closeAreaMaterialMappingModal: () => void;

  editingSnapshot: AreaMaterialSnapshot | null;
  startEditingSnapshot: (snapshot: AreaMaterialSnapshot) => void;
  clearEditingSnapshot: () => void;

  currentAreaMaterialMapping: Record<string, string>;
  currentAreaMaterialLabels: Record<string, string>;
  reportedMaterialsAtMappingTime: Record<string, string>;
  miniMapAreaColors: Record<string, string>;
  handleAreaMaterialUpdate: (area: string, material: string | null) => void;
  handleSnapshotMappingUpdate: (area: string, material: string | null) => void;
  handleDeleteSnapshot: (snapshot: AreaMaterialSnapshot) => void;
  handleMappingSnapshotClick: (snapshot: AreaMaterialSnapshot) => void;
  editingSnapshotCurrentMapping: Record<string, string>;
}>;

export const useManualReviewHotkeys = ({
  onVerifyAndNext,
  location,
  railcarSlotMaterials,
}: ManualReviewHotkeysProps): ManualReviewHotkeysResult => {
  const { getHotkey } = useHotkeyContext();
  const { cycleAllAngles, resetAllToAuto } = useVideoPlayerGroupContext();
  const {
    handleStartRecording,
    handleStopRecording,
    handleAddFlag,
    handleUpdateEvent,
    handleSetMaterial,
    handleSetArea,
    handleReverseEventType,
    handleDeleteEvent,
    handleUndo,
    handleRedo,
    allEvents,
    selectedEventId,
    recordingEventId,
    getAreaMaterialMappingAtTime,
    updateAreaMaterialMapping,
    deleteAreaMaterialSnapshot,
    videoStart,
  } = useManualReviewEvents();

  const { currentTime } = useVideoControlState();
  const areaHotkeyBehaviour = useManualReviewAreaHotkeyBehaviour();

  const { materials } = useMaterials();

  const { enableScope, disableScope } = useHotkeysContext();
  useEffect(() => {
    enableScope(MANUAL_REVIEW_SCOPE);
    enableScope(EVENT_LABELLING_DELETE_SCOPE);
    return () => {
      disableScope(MANUAL_REVIEW_SCOPE);
      disableScope(EVENT_LABELLING_DELETE_SCOPE);
    };
  }, [enableScope, disableScope]);

  const [pendingAreaSelection, setPendingAreaSelection] =
    useState<PendingAreaSelection | null>(null);

  const [isAreaMaterialMappingOpen, setIsAreaMaterialMappingOpen] =
    useState(false);
  const [editingSnapshot, setEditingSnapshot] =
    useState<AreaMaterialSnapshot | null>(null);

  const filteredMaterials = useMemo(() => {
    return (
      materials?.filter((material) => material.labelling_name !== null) ?? []
    );
  }, [materials]);

  const currentTimestamp = useMemo(
    () => videoStart.add(currentTime, "second"),
    [videoStart, currentTime]
  );

  const currentAreaMaterialMapping = useMemo(
    (): Record<string, string> =>
      getAreaMaterialMappingAtTime(currentTimestamp),
    [getAreaMaterialMappingAtTime, currentTimestamp]
  );

  const reportedRailcarMaterials = useReportedRailcarMaterials(
    railcarSlotMaterials,
    currentTimestamp
  );

  const reportedMaterialsAtMappingTime = useReportedRailcarMaterials(
    railcarSlotMaterials,
    editingSnapshot?.timestamp ?? currentTimestamp
  );

  const currentAreaMaterialLabels = useAreaMaterialLabels(
    currentAreaMaterialMapping,
    reportedRailcarMaterials
  );

  const miniMapAreaColors = useAreaMaterialColors(
    currentAreaMaterialMapping,
    reportedRailcarMaterials
  );

  const editingSnapshotCurrentMapping = useMemo((): Record<string, string> => {
    if (!editingSnapshot) return {};
    return getAreaMaterialMappingAtTime(editingSnapshot.timestamp);
  }, [editingSnapshot, getAreaMaterialMappingAtTime]);

  const clearPendingAreaSelection = useCallback(() => {
    setPendingAreaSelection(null);
  }, []);

  const handleAreaSelect = useCallback(
    (area: string) => {
      if (pendingAreaSelection) {
        const updates: { area: string; material?: string | null } = { area };
        if (pendingAreaSelection.eventType === "lift") {
          updates.material = currentAreaMaterialMapping[area] ?? null;
        }
        handleUpdateEvent(
          pendingAreaSelection.eventId,
          updates,
          "withConnected"
        );
        setPendingAreaSelection(null);
      }
    },
    [pendingAreaSelection, handleUpdateEvent, currentAreaMaterialMapping]
  );

  const openAreaMaterialMappingModal = useCallback(() => {
    setIsAreaMaterialMappingOpen(true);
  }, []);

  const closeAreaMaterialMappingModal = useCallback(() => {
    setIsAreaMaterialMappingOpen(false);
    setEditingSnapshot(null);
  }, []);

  const startEditingSnapshot = useCallback((snapshot: AreaMaterialSnapshot) => {
    setEditingSnapshot(snapshot);
  }, []);

  const clearEditingSnapshot = useCallback(() => {
    setEditingSnapshot(null);
  }, []);

  const handleAreaMaterialUpdate = useCallback(
    (area: string, material: string | null) => {
      updateAreaMaterialMapping(currentTimestamp, area, material);
    },
    [updateAreaMaterialMapping, currentTimestamp]
  );

  const handleSnapshotMappingUpdate = useCallback(
    (area: string, material: string | null) => {
      if (editingSnapshot) {
        updateAreaMaterialMapping(editingSnapshot.timestamp, area, material);
      }
    },
    [updateAreaMaterialMapping, editingSnapshot]
  );

  const handleDeleteSnapshot = useCallback(
    (snapshot: AreaMaterialSnapshot) => {
      deleteAreaMaterialSnapshot(snapshot.timestamp);
      setEditingSnapshot(null);
    },
    [deleteAreaMaterialSnapshot]
  );

  const handleMappingSnapshotClick = useCallback(
    (snapshot: AreaMaterialSnapshot) => {
      setEditingSnapshot(snapshot);
    },
    []
  );

  // Noop for snapToCandidate - not used in manual review
  const handleSnapToCandidate = useCallback(() => {
    // Not applicable for manual review
  }, []);

  const {
    isAreaModeActive,
    isShiftHeld,
    binEntryActive,
    binBuffer,
    binEntryAvailable,
  } = useLabellingKeyboard({
    events: allEvents,
    selectedEventId,
    recordingEventId,
    videoStart,
    filteredMaterials,
    altAreaBehavior: areaHotkeyBehaviour,
    location,
    callbacks: {
      handleStartRecording,
      handleStopRecording,
      handleDeleteEvent,
      handleSetMaterial,
      handleSetArea,
      handleReverseEventType,
      handleAddFlag,
      handleSnapToCandidate,
      setPendingAreaSelection,
      openAreaMaterialMappingModal,
      cycleAllAngles,
      resetAllToAuto,
    },
  });

  // Manual review specific: verify window hotkey
  const verifyWindowKey = getHotkey("verifyWindow");
  useHotkeys(
    verifyWindowKey,
    () => {
      void onVerifyAndNext();
    },
    { enableOnFormTags: false, scopes: [MANUAL_REVIEW_SCOPE] },
    [verifyWindowKey, onVerifyAndNext]
  );

  useHotkeys(
    "mod+z",
    (e) => {
      e.preventDefault();
      handleUndo();
    },
    { scopes: [MANUAL_REVIEW_SCOPE], enableOnFormTags: true },
    [handleUndo]
  );

  useHotkeys(
    "mod+shift+z, mod+y",
    (e) => {
      e.preventDefault();
      handleRedo();
    },
    { scopes: [MANUAL_REVIEW_SCOPE], enableOnFormTags: true },
    [handleRedo]
  );

  const deleteEventKey = getHotkey("deleteEvent");
  useHotkeys(
    deleteEventKey,
    () => {
      const eventId = selectedEventId ?? allEvents[allEvents.length - 1]?.id;
      if (eventId) {
        handleDeleteEvent(eventId);
      }
    },
    {
      keydown: true,
      preventDefault: true,
      scopes: [EVENT_LABELLING_DELETE_SCOPE],
      enabled: !binEntryActive,
    },
    [
      deleteEventKey,
      allEvents,
      selectedEventId,
      handleDeleteEvent,
      binEntryActive,
    ]
  );

  return {
    pendingAreaSelection,
    clearPendingAreaSelection,
    handleAreaSelect,
    isAreaModeActive,
    isShiftHeld,
    binEntryActive,
    binBuffer,
    binEntryAvailable,
    filteredMaterials,
    isAreaMaterialMappingOpen,
    openAreaMaterialMappingModal,
    closeAreaMaterialMappingModal,
    editingSnapshot,
    startEditingSnapshot,
    clearEditingSnapshot,
    currentAreaMaterialMapping,
    currentAreaMaterialLabels,
    reportedMaterialsAtMappingTime,
    miniMapAreaColors,
    handleAreaMaterialUpdate,
    handleSnapshotMappingUpdate,
    handleDeleteSnapshot,
    handleMappingSnapshotClick,
    editingSnapshotCurrentMapping,
  };
};
